import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, DraftingCompass, Wrench, LayoutDashboard, Activity, Headset,
  ArrowRight, Sun,
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SERVICES, SOLAR_STATS } from "@/lib/siteContent";
import { useContent, resolveImg, isLocalImg } from "@/lib/content";
import { IMAGES } from "@/lib/constants";

const EASE = [0.16, 1, 0.3, 1];
const SERVICE_ICONS = [Zap, DraftingCompass, Wrench, LayoutDashboard, Activity, Headset];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, delay, ease: EASE },
});

export default function HomePage() {
  useSeo(
    "HB Charger | Infraestrutura para recarga de veículos elétricos — HB Solar",
    "Carregadores rápidos DC, eletropostos, projeto elétrico, instalação e plataforma de gerenciamento para mobilidade elétrica. HB Solar / HB Charger na Bahia."
  );
  const { textos, imagens } = useContent();

  return (
    <>
      <section data-testid="hero-ev-section" className="relative grain overflow-hidden bg-[#0B192C] pt-[76px]">
        <div className="pointer-events-none absolute -top-32 left-1/3 h-[420px] w-[820px] rounded-full bg-blue-600/15 blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300"
            >
              <Zap className="h-3.5 w-3.5" />
              HB Charger — Mobilidade elétrica
            </motion.p>
            <motion.h1
              data-testid="hero-ev-title"
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-white"
            >
              {textos.heroEvTitulo}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
              {textos.heroEvSubtitulo}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                data-testid="hero-chargers-button"
                to="/carregadores"
                className="group inline-flex items-center gap-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-extrabold px-7 py-4 text-base transition-colors duration-300 shadow-[0_0_36px_rgba(245,158,11,0.35)]"
              >
                Conheça nossos carregadores
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                data-testid="hero-quote-button"
                to="/contato"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/25 text-white font-bold px-7 py-4 text-base hover:bg-white/10 transition-colors duration-300"
              >
                Solicitar orçamento
              </Link>
            </motion.div>
          </div>

          <motion.figure
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: EASE }}
            className="relative rounded-3xl overflow-hidden border border-white/15 shadow-[0_40px_80px_rgba(0,0,0,0.45)]"
          >
            <img
              src={resolveImg(imagens.heroEv)}
              alt="Estação de recarga HB Charger"
              className={`h-[420px] lg:h-[500px] w-full ${
                isLocalImg(imagens.heroEv) ? "object-contain bg-white p-6" : "object-cover"
              }`}
              loading="eager"
            />
          </motion.figure>
        </div>
      </section>

      <section data-testid="services-section" className="py-24 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...reveal()} className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-500 mb-4">O que fazemos</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              {textos.homeServicosTitulo}
            </h2>
          </motion.div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <motion.article
                  key={s.titulo}
                  {...reveal(i * 0.06)}
                  className="group rounded-3xl border border-slate-200/80 bg-[#FAFAF9] p-8 hover:shadow-[0_24px_48px_rgba(11,25,44,0.10)] hover:-translate-y-1 transition-all duration-500"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B192C] text-amber-400 transition-transform duration-500 group-hover:scale-110">
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-[#0B192C]">{s.titulo}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF9] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...reveal()} className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg">
            <img src={IMAGES.hero} alt="Geração de energia solar fotovoltaica" className="h-72 w-full object-cover" loading="lazy" />
          </motion.div>
          <motion.div {...reveal(0.1)}>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-500 mb-4">
              <Sun className="inline h-4 w-4 mr-1 -mt-0.5" /> HB Solar — Energia solar
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              A mesma engenharia que já entregou {SOLAR_STATS.projetos} projetos solares
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              A HB Solar atua há anos em geração de energia solar fotovoltaica na Bahia.
              Essa experiência em engenharia elétrica é a base da divisão HB Charger,
              dedicada à infraestrutura de recarga para veículos elétricos.
            </p>
            <Link
              to="/energia-solar"
              className="mt-6 inline-flex items-center gap-2 text-sm font-extrabold text-amber-600 hover:text-amber-500 transition-colors"
            >
              Ver soluções de energia solar
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.div
            {...reveal()}
            className="rounded-[2rem] bg-gradient-to-br from-amber-400 to-amber-500 p-10 sm:p-14 text-center shadow-[0_30px_60px_rgba(245,158,11,0.25)]"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C]">
              {textos.homeCtaTitulo}
            </h2>
            <p className="mt-3 mx-auto max-w-xl text-base font-semibold text-[#0B192C]/70">
              {textos.homeCtaTexto}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                data-testid="home-simulate-cta"
                to="/simule"
                className="rounded-full bg-[#0B192C] text-white font-extrabold px-8 py-4 text-base hover:bg-[#12243d] transition-colors duration-300"
              >
                Simule seu projeto
              </Link>
              <Link
                to="/contato"
                className="rounded-full border-2 border-[#0B192C]/30 text-[#0B192C] font-extrabold px-8 py-4 text-base hover:bg-white/30 transition-colors duration-300"
              >
                Falar com o comercial
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
